import { z } from 'zod';

export const AnalyticsDataSchema = z.object({
  weeklyTasksData: z.array(
    z.object({
      day: z.string(),
      tasks: z.number(),
      quality: z.number(),
    })
  ),
  roleDistribution: z.array(
    z.object({
      role: z.string(),
      count: z.number(),
      color: z.string(),
    })
  ),
  performanceTrends: z.array(
    z.object({
      month: z.string(),
      accuracy: z.number(),
      throughput: z.number(),
      retention: z.number(),
    })
  ),
  workflowMetrics: z.array(
    z.object({
      workflow: z.string(),
      tasksCompleted: z.number(),
      avgAccuracy: z.number(),
      utilization: z.number(),
    })
  ),
});
export type AnalyticsData = z.infer<typeof AnalyticsDataSchema>;

export const AnalyticsTriggerResponseSchema = z.object({
  message: z.string(),
  statementId: z.string(),
});
export type AnalyticsTriggerResponse = z.infer<
  typeof AnalyticsTriggerResponseSchema
>;

export const AnalyticsQueryResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum([
    'PENDING',
    'RUNNING',
    'FINISHED',
    'FAILED',
    'PICKED',
    'STARTED',
  ]),
  error: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  duration: z.number().int().optional(),
  hasResultSet: z.boolean(),
});
export type AnalyticsQueryResponse = z.infer<
  typeof AnalyticsQueryResponseSchema
>;

// 1. getActiveUsersQuery
export const ActiveUsersResultSchema = z.array(
  z.object({
    active_user_count: z.coerce.number(),
  })
);

// 2. getTasksWorkedOnQuery
export const TasksWorkedOnResultSchema = z.array(
  z.object({
    unique_tasks_count: z.coerce.number(),
  })
);

// 3. getWeeklyCompletionHistogramQuery
export const WeeklyCompletionHistogramResultSchema = z.array(
  z.object({
    day: z.coerce.date(),
    completed_count: z.coerce.number(),
  })
);

// 4. getCompletionsByWorkflowQuery
export const CompletionsByWorkflowResultSchema = z.array(
  z.object({
    workflow_name: z.string(),
    completed_count: z.coerce.number(),
  })
);

// 5. getActiveVsWallClockTimeQuery
export const ActiveVsWallClockTimeResultSchema = z.array(
  z.object({
    global_utilization_ratio: z.coerce.number().nullable(),
  })
);

// 6. getAnnotationDwellTimeQuery
export const AnnotationDwellTimeResultSchema = z.array(
  z.object({
    avg_seconds_in_annotation: z.coerce.number().nullable(),
  })
);

// 7. getThroughputRateQuery
export const ThroughputRateResultSchema = z.array(
  z.object({
    tasks_per_active_hour: z.coerce.number().nullable(),
  })
);

// 8. getAvgActiveTimePerTaskQuery
export const AvgActiveTimePerTaskResultSchema = z.array(
  z.object({
    average_seconds_per_task: z.coerce.number().nullable(),
  })
);

// 9. getTotalActiveTimeQuery
export const TotalActiveTimeResultSchema = z.array(
  z.object({
    total_active_seconds: z.coerce.number(),
  })
);

// 10. getToolUsageDistributionQuery
export const ToolUsageDistributionResultSchema = z.array(
  z.object({
    tool_alias: z.string(),
    usage_count: z.coerce.number(),
  })
);

export const RawAnalyticsResponseSchema = z.tuple([
  ActiveUsersResultSchema,
  TasksWorkedOnResultSchema,
  WeeklyCompletionHistogramResultSchema,
  CompletionsByWorkflowResultSchema,
  ActiveVsWallClockTimeResultSchema,
  AnnotationDwellTimeResultSchema,
  ThroughputRateResultSchema,
  AvgActiveTimePerTaskResultSchema,
  TotalActiveTimeResultSchema, // Index 8
  ToolUsageDistributionResultSchema, // Index 9
]);

export type RawAnalyticsResponse = z.infer<typeof RawAnalyticsResponseSchema>;

export interface AnalyticsFilterParams {
  workflow_name?: string;
  instruction_name?: string;
  tool_alias?: string;
  task_type?: string;
  request_id?: string;
}

export const AnalyticsFilterOptionsResponseSchema = z.object({
  workflow_name: z.array(z.string()).optional(),
  instruction_name: z.array(z.string()).optional(),
  tool_alias: z.array(z.string()).optional(),
  task_type: z.array(z.string()).optional(),
  request_id: z.array(z.string()).optional(),
});
export type AnalyticsFilterOptionsResponse = z.infer<
  typeof AnalyticsFilterOptionsResponseSchema
>;
